import { useEffect, useState, useRef, useCallback } from "react";
import { LOGIN_IN_PROGRESS_VIEWS } from "./login-views";
import { useSession as useNextAuthSession } from "next-auth/react";
import { useUser, useUI, useTheme, useAppSession, useRouterContext } from "../../contexts";
import type { LoginView } from "../../contexts/UIProvider";
import { LS_Get, Cookies } from "../../utils/local-storage";
import { emailFromSearch } from "./login-prefill";
import { changeHash, removeSlashes, $$, createNotification } from "../../utils/dom";
import { checkPhone } from "../utils";
import { hasFirstName } from "../../utils/person-name";
import shared from "../../data/js/shared";
import { UNDELIVERABLE_SMS_MESSAGES } from "../../utils/sms-delivery-messages";
import { CODE_EXPIRED_ERROR } from "../../utils/auth-signin-errors";

import { useRecaptcha, useLoginForm, useLoginAuth } from "./index";
import { PHONE_NEEDS_EMAIL } from "./useLoginAuth";
import type { LoginFormFields, FormErrors, FormFocus } from "./useLoginForm";

import {
	trackComplete,
	trackFunnelEvent,
	trackFieldChange,
	trackError,
	resetFunnelSession,
	trackAbandoned,
	trackModalOpened,
	trackModalClosed,
} from "../../utils/track-funnel";

/** When the code step asks whether the text arrived, counted from when it opens. */
export const CODE_DELIVERY_CHECKS_MS = [4000, 8000, 15000, 30000];

// Allowed view states for the login flow
export const ALLOWED_VIEW_STATES = [
	"login",
	"otp",
	"mobile",
	"welcome",
	"otp-error",
	"login-otp",
	"more-info",
	"phone-verified",
	"loggedin",
];

/**
 * useLoginFlow Hook
 *
 * Extracts all logic from the Login component including:
 * - Context hooks (useUser, useUI, useTheme, useAppSession, useRouterContext)
 * - NextAuth session management
 * - Form state management (useLoginForm)
 * - Authentication operations (useLoginAuth)
 * - reCAPTCHA handling (useRecaptcha)
 * - All useEffect blocks for initialization and monitoring
 * - Event handlers (handleSubmit, closeLogin, handleGoogleLogin, etc.)
 *
 * @returns Object containing all state, handlers, and refs needed by the Login component
 */
export const useLoginFlow = () => {
	const { query, asPath, push } = useRouterContext();
	const fireworksRef = useRef<{ start: () => void } | null>(null);
	// Track if we're verifying email (vs login OTP) when in login-otp view
	const isEmailVerificationOtpRef = useRef(false);
	/**
	 * When the more-info step opened, or null when it is not open.
	 *
	 * A ref rather than state: it is read from inside the hashchange handler and
	 * the pagehide listener, neither of which should re-run because it changed.
	 */
	const moreInfoOpenRef = useRef<number | null>(null);

	// Context hooks
	const { data: nextAuthSession, status: nextAuthStatus } = useNextAuthSession();
	const { profile, loggedIn } = useUser();
	const {
		loginView,
		loginOpen,
		setLoginView,
		setLoginOpen,
		setPopupContents,
		setPopupOpen,
		loginPrefill,
		setLoginPrefill,
	} = useUI();
	const { theme, hydrated: themeHydrated, toggleTheme } = useTheme();
	const { tempShippingAddress } = useAppSession();

	// Custom hooks
	const { executeRecaptcha, initRecaptcha } = useRecaptcha();

	// Callback fired on first form interaction (for funnel tracking)
	const handleLoginInteraction = () => {
		const hash = window.location.hash.slice(1);
		const method = "email_otp";

		const redirectUrl = (query.returnTo as string) || asPath.split("#")[0];

		resetFunnelSession("login");
		trackFunnelEvent(
			"login",
			"login_started",
			{
				method,
				redirect_url: redirectUrl,
				referrer: document.referrer || "",
			},
			{ immediate: true }
		);
	};

	// Track field changes on more-info view
	const handleFieldChange = (fieldName: string, value: string) => {
		if (loginView === "more-info" && (fieldName === "name" || fieldName === "email")) {
			trackFieldChange("login", fieldName, value ? "filled" : "empty", "more-info");
		}
	};

	const {
		fields,
		errors,
		focus,
		filled,
		otpCode,
		setFields,
		setErrors,
		setFocus,
		setFilled,
		setOTPCode,
		updateField,
		updateFields,
		setFieldFocus,
		validateAll,
		triggerInteraction,
		hasInteracted,
	} = useLoginForm({ onInteraction: handleLoginInteraction, onFieldChange: handleFieldChange });

	/**
	 * Adopt an identifier another surface left for us.
	 *
	 * Checkout sets this after a guest buys: the webhook has already created
	 * their account under the address they typed into the order, so the modal
	 * opens holding it rather than asking them to remember and retype it. Read
	 * once and cleared, so it cannot leak into a later, unrelated login.
	 */
	useEffect(() => {
		if (!loginPrefill) return;
		// Adopts a value handed over by another surface through context: not
		// derivable during render, and consumed exactly once. (The
		// `react-hooks/set-state-in-effect` disable that used to sit here no
		// longer matched anything and was reported as unused.)
		setFields((prev) => ({ ...prev, phone: loginPrefill }));
		setLoginPrefill(null);
	}, [loginPrefill, setFields, setLoginPrefill]);

	const {
		sendLoginCode,
		checkCodeDelivery,
		verifyLoginOTP,
		sendEmailVerification,
		verifyEmailOTP,
		loginWithGoogle,
		loginWithFacebook,
		loginWithApple,
		cleanupLoginSuccess,
	} = useLoginAuth({
		fields,
		otpCode,
		executeRecaptcha,
		onInteraction: triggerInteraction,
	});

	// Local state
	const [submitting, setSubmitting] = useState(false);

	/**
	 * What went wrong with the code just entered, shown under the code boxes.
	 *
	 * A wrong code used to replace the whole form with a separate "not the
	 * correct digits" screen -- a dark-mode switch and a Try Again that started
	 * the login over. Now the code step stays, the boxes clear, and this says
	 * why. Cleared as soon as a digit is typed.
	 */
	const [otpError, setOtpError] = useState<string | null>(null);
	const changeOTPCode = useCallback((code: string) => {
		setOtpError(null);
		setOTPCode(code);
	}, [setOTPCode]);

	/**
	 * How many codes the code step has re-sent. A resend bumps it so the
	 * delivery checks below start over for the new text rather than stay
	 * finished on the old one.
	 */
	const [codeResends, setCodeResends] = useState(0);

	/**
	 * Ask whether the login text arrived, a few times after the code step opens.
	 *
	 * Twilio takes a text for a landline as readily as any other, so the send
	 * says "sent" and the person waits for a code that cannot come -- 32 of the
	 * last 1,000 texts on 2026-09-23, none of them ever told. Twilio knows within
	 * seconds. Stops at the first final answer, or when the step closes.
	 */
	useEffect(() => {
		if (loginView !== "login-otp" || isEmailVerificationOtpRef.current) return;
		let stopped = false;
		let timer: ReturnType<typeof setTimeout> | undefined;
		// One after another, never two at once: the next check is scheduled only
		// once the last one has answered.
		const schedule = (i: number) => {
			if (i >= CODE_DELIVERY_CHECKS_MS.length) return;
			const wait = CODE_DELIVERY_CHECKS_MS[i] - (i > 0 ? CODE_DELIVERY_CHECKS_MS[i - 1] : 0);
			timer = setTimeout(() => void check(i), wait);
		};
		const check = async (i: number) => {
			const result = await checkCodeDelivery();
			if (stopped) return;
			if (result?.delivery === "undeliverable" && result.message) {
				setOtpError(result.message);
				return;
			}
			if (result?.delivery === "delivered") return;
			schedule(i + 1);
		};
		schedule(0);
		return () => {
			stopped = true;
			clearTimeout(timer);
		};
	}, [loginView, checkCodeDelivery, codeResends]);

	/**
	 * Send another code from the code step, without leaving it.
	 *
	 * "Send a new code" was a bare href="#" link: it cleared the hash, the view
	 * fell back to the first screen, and nothing was sent until the person found
	 * Continue and pressed it again. This sends to the same place the first
	 * code went -- the login code, or the email check from the more-info step.
	 * Resolves true when a code went out; a refusal has already said why.
	 */
	const resendCode = useCallback(async (): Promise<boolean> => {
		const verifyingEmail = isEmailVerificationOtpRef.current;
		trackFunnelEvent("login", "code_resend_requested", {
			resend: true,
			purpose: verifyingEmail ? "email_capture" : "login",
		});
		setOtpError(null);

		let sent: boolean;
		if (verifyingEmail) {
			const result = await sendEmailVerification(profile);
			sent = result.success;
			// sendLoginCode reports its own refusals; this one leaves it to the caller.
			if (!sent && result.error) {
				createNotification({
					type: "error",
					msg: result.error === "network_error" ? "Network error. Please try again." : result.error,
				});
			}
		} else {
			sent = (await sendLoginCode()).success;
		}

		if (sent) setCodeResends((n) => n + 1);
		return sent;
	}, [sendEmailVerification, sendLoginCode, profile]);

	/**
	 * Handle hash changes for view state management
	 */
	const handleHashChange = useCallback(() => {
		const hash = window.location.hash.slice(1);

		/**
		 * Leaving the more-info step without filling it in.
		 *
		 * The step had an arrival event and a submission event and nothing in
		 * between, so 371 of the 379 people who reached it in two weeks left no
		 * trace at all — and "did they close it or did it close itself" was a
		 * question the data could not answer. `modal_closed` did not cover it:
		 * exactly one of those 379 ever emitted one.
		 *
		 * `to` is the hash they went to, which is the difference between the two
		 * explanations. `""` is a deliberate close; `loggedin` means the success
		 * screen took the view away from them.
		 */
		if (moreInfoOpenRef.current && hash !== "more-info") {
			const openedAt = moreInfoOpenRef.current;
			moreInfoOpenRef.current = null;
			trackFunnelEvent("login", "more_info_left", {
				to: hash,
				seconds_open: Math.round((Date.now() - openedAt) / 1000),
				has_name: !!profile?.firstName,
				has_email: !!profile?.email,
			}, { immediate: true });
		}

		// Early exit for "more-info" if user not logged in or profile is complete
		if (hash === "more-info") {
			if (!loggedIn || (profile && hasFirstName(profile) && profile.email)) {
				changeHash("");
				return;
			}
		}

		if (ALLOWED_VIEW_STATES.includes(hash)) {
			/**
			 * Reaching the more-info step, which nothing reported until now.
			 *
			 * The login funnel's More Info node counts `more_info_started` and no
			 * code has ever emitted it: over 30 days it read 0 while Profile Done,
			 * the step AFTER it, read 22. A funnel where nobody arrives and 22
			 * people finish is not a funnel.
			 *
			 * Emitted here rather than at the two `changeHash("more-info")` calls
			 * because this is where the view actually becomes more-info, which also
			 * catches someone landing on the hash directly, and it sits below the
			 * guard above so a signed-out or already-complete profile bounced back
			 * out is not counted as having reached the step.
			 */
			if (hash === "more-info") {
				moreInfoOpenRef.current = Date.now();
				trackFunnelEvent("login", "more_info_started", {
					has_name: !!profile?.firstName,
					has_email: !!profile?.email,
				});
			}

			setLoginView(hash as LoginView);
			setLoginOpen(true);
		}

		if (hash === "") {
			setLoginView("login");
		}

		if (hash === "loggedin") {
			fireworksRef.current?.start();
		}

		if (hash === "login-otp") {
			setTimeout(() => {
				const otp = $$(".otp-number-input");
				if (otp.length) otp[0].focus();
			}, 1000);
		}
	}, [loggedIn, profile, setLoginView, setLoginOpen]);

	/**
	 * Close the login modal
	 */
	const closeLogin = () => {
		const didInteract = hasInteracted();
		/**
		 * Abandoned means interacted AND did not get in -- the comment always said
		 * so, the condition never checked the second half.
		 *
		 * Signing in closes the modal, so every successful login emitted
		 * `abandoned` on the way out. On production all 11 login abandonments in
		 * 14 days belong to sessions that had already emitted `complete`, in the
		 * order `complete, abandoned, modal_closed`: a node that was wrong 100% of
		 * the time it fired.
		 */
		const didAbandon = didInteract && !loggedIn;
		if (didAbandon) {
			trackAbandoned("login", {
				last_view: loginView,
			});
		}
		// Always track modal closed for visibility
		trackModalClosed("login", {
			had_interaction: didInteract,
			last_view: loginView,
			completed: loggedIn,
		});

		// Reset email verification flag if closing from OTP view
		isEmailVerificationOtpRef.current = false;

		const sp = $$(".s2nPlayerContainer")[0];
		if (sp) sp.style.display = "block";

		let rq = query.returnTo;
		if (rq && typeof rq === "string") {
			rq = rq.split("#")[0];
		}

		if (asPath.includes("login")) {
			if (rq && rq !== "/logout") {
				push(removeSlashes(rq));
			} else {
				push("/");
			}
		}

		if (asPath === "/logout") {
			push("/");
		}

		setLoginOpen(false);
		changeHash("");
	};

	/**
	 * Submit the form based on current view
	 */
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		const isValid = validateAll(loginView);
		if (!isValid || submitting) return;

		setSubmitting(true);

		try {
			switch (loginView) {
				case "login":
					await sendLoginCode();
					break;
				case "mobile":
					// Mobile phone verification flow
					await sendLoginCode();
					break;
				case "more-info": {
					// Send email verification OTP (passes profile to check if already verified)
					const result = await sendEmailVerification(profile);
					if (result.success) {
						if (result.emailVerificationRequired) {
							// Need to verify the new email - use login-otp view
							isEmailVerificationOtpRef.current = true;
							setLoginView("login-otp");
							changeHash("login-otp");
						} else {
							// Email already verified, go straight to welcome
							changeHash("welcome");
							await cleanupLoginSuccess();
						}
					} else if (result.error) {
						// Show error to user
						const { createNotification } = await import("../../utils/dom");
						createNotification({
							type: "error",
							msg: result.error === 'network_error'
								? "Network error. Please try again."
								: result.error,
						});
					}
					break;
				}
				case "login-otp": {
					// Email verification OTP (from more-info flow), or the login OTP.
					const verifyingEmail = isEmailVerificationOtpRef.current;
					const result = verifyingEmail ? await verifyEmailOTP(otpCode) : await verifyLoginOTP();
					if (result?.success) {
						isEmailVerificationOtpRef.current = false;
						break;
					}
					// A new number is proven; the account comes from the email entered next.
					if (result?.error === PHONE_NEEDS_EMAIL) {
						setOTPCode('');
						setFields((prev) => ({ ...prev, phone: '' }));
						setLoginView("login");
						changeHash("login");
						break;
					}
					// Stay on the code step. The email flag stays set too: it was
					// cleared on ANY attempt, so a retry after one wrong email code
					// was sent to the login verifier instead.
					const error = result?.error ?? '';
					if (error.startsWith('Too many wrong codes')) {
						setOTPCode('');
						setOtpError(error);
					} else if (error === 'network_error') {
						setOtpError(shared.login.otpNetworkError);
					} else if (UNDELIVERABLE_SMS_MESSAGES.has(error)) {
						// The text never arrived: say why rather than "wrong code".
						setOTPCode('');
						setOtpError(error);
					} else if (error === CODE_EXPIRED_ERROR) {
						// Expired, used or never sent: retyping cannot help, a new code can.
						setOTPCode('');
						setOtpError(shared.login.otpExpiredCode);
					} else if (
						error.includes('Invalid verification code') ||
						(verifyingEmail && error !== 'unauthorized' && error !== 'email_taken')
					) {
						setOTPCode('');
						setOtpError(shared.login.otpWrongCode);
					}
					break;
				}
			}
		} finally {
			setSubmitting(false);
		}
	};

	/**
	 * Handle social login with cookie cleanup
	 */
	const handleGoogleLogin = async () => {
		Cookies.delete("g_state");
		await loginWithGoogle();
	};

	// Initialize
	useEffect(() => {
		initRecaptcha();

		// A link that names the email (/login?email=...) wins over the last value saved
		// on this device: it is how an email we sent says which account to sign in to.
		const linkEmail = emailFromSearch(window.location.search);
		// Load saved phone/country code
		const phone = LS_Get<string>("phone");
		const countryCode = LS_Get<{ code: string; name: string; phone: number }>("countryCode");
		if (linkEmail) {
			updateFields({ phone: linkEmail });
		} else if (phone && countryCode) {
			updateFields({ phone, countryCode });
		}

		handleHashChange();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Track modal opened only when modal is actually visible and user is not authenticated
	const hasTrackedModalOpened = useRef(false);
	useEffect(() => {
		// Skip tracking for authenticated users - modal will auto-close for them
		if (nextAuthStatus === "authenticated") {
			return;
		}

		if (loginOpen && !hasTrackedModalOpened.current) {
			hasTrackedModalOpened.current = true;

			// Track modal opened (informational only - doesn't start funnel)
			// Funnel starts when user interacts with the form
			const hash = window.location.hash.slice(1);
			const method = "email_otp";

			const redirectUrl = (query.returnTo as string) || asPath.split("#")[0];
			const referrer = document.referrer || "";

			trackModalOpened("login", {
				method,
				redirect_url: redirectUrl,
				referrer,
				hash,
			});
		} else if (!loginOpen) {
			// Reset tracking when modal closes so it can track again when reopened
			hasTrackedModalOpened.current = false;
		}
	}, [loginOpen, nextAuthStatus, query.returnTo, asPath]);

	// Sync profile to form (only overwrite empty fields to preserve user input)
	useEffect(() => {
		if (profile?.id) {
			const updates: Partial<typeof fields> = {};

			// Only sync name if profile has one
			if (profile.firstName) {
				updates.name = profile.firstName + (profile.lastName ? " " + profile.lastName : "");
			}

			// Only sync email if profile has one (don't overwrite user-entered email)
			if (profile.email) {
				updates.email = profile.email;
			}

			if (Object.keys(updates).length > 0) {
				updateFields(updates);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [profile]);

	// Listen for hash changes and re-evaluate when profile changes
	useEffect(() => {
		handleHashChange();
		window.addEventListener("hashchange", handleHashChange);
		return () => {
			window.removeEventListener("hashchange", handleHashChange);
		};
	}, [profile, handleHashChange]);

	/**
	 * The other way out of the more-info step: leaving the page entirely.
	 *
	 * 83 of the 379 who reached it were on a different path within two minutes —
	 * a `returnTo` redirect, or the sell flow they logged in from — and a
	 * navigation takes the `#more-info` hash with it without ever firing
	 * `hashchange`. Without this those people are indistinguishable from someone
	 * who sat and stared at it.
	 *
	 * `pagehide`, not `beforeunload`: it is the one that fires on mobile Safari,
	 * and `immediate` so the event is flushed rather than left in the 2-second
	 * batch that the unload discards.
	 */
	useEffect(() => {
		const onPageHide = () => {
			if (!moreInfoOpenRef.current) return;
			const openedAt = moreInfoOpenRef.current;
			moreInfoOpenRef.current = null;
			trackFunnelEvent("login", "more_info_left", {
				to: "unload",
				seconds_open: Math.round((Date.now() - openedAt) / 1000),
			}, { immediate: true });
		};
		window.addEventListener("pagehide", onPageHide);
		return () => {
			window.removeEventListener("pagehide", onPageHide);
		};
	}, []);

	// Monitor NextAuth session for login cleanup and social login completion
	useEffect(() => {
		if (nextAuthStatus === "loading") return;

		if (nextAuthStatus === "authenticated" && nextAuthSession && loggedIn) {
			// Check for pending social login to track completion
			const pendingStr = sessionStorage.getItem("pending_social_login");
			if (pendingStr) {
				try {
					const pending = JSON.parse(pendingStr);
					const user = nextAuthSession.user;
					trackComplete("login", {
						user_id: user?.id || nextAuthSession?.profile?.id,
						email: user?.email,
						method: `social_${pending.provider}`,
						is_new_user: false,
						redirect_url: pending.callback_url || "/",
						provider: pending.provider,
						duration_ms: Date.now() - pending.started_at,
					});
					sessionStorage.removeItem("pending_social_login");
				} catch (e) {
					// Only JSON.parse, a read off what it returned, and storage are in the
					// try: each throws an Error (SyntaxError, TypeError, DOMException).
					trackError("login", "social_login_tracking", (e as Error).message);
				}
			}
			void cleanupLoginSuccess();
		}
	}, [nextAuthSession, nextAuthStatus, loggedIn, cleanupLoginSuccess]);

	// Track OAuth failures from callback error param
	useEffect(() => {
		const error = query.error as string;
		if (error) {
			// Get provider from pending social login if available
			let provider = "unknown";
			const pendingStr = sessionStorage.getItem("pending_social_login");
			if (pendingStr) {
				try {
					const pending = JSON.parse(pendingStr);
					provider = pending.provider;
					sessionStorage.removeItem("pending_social_login");
				} catch (parseError) {
					// The stored hand-off is unreadable, so this OAuth failure cannot
					// be attributed to a provider. Said out loud rather than
					// swallowed: "unknown" otherwise reads as "we never stored one",
					// and the two have different causes — one is a user who arrived
					// without starting a social login, the other is something writing
					// a malformed payload, which nothing would ever have reported.
					provider = "unparseable";
					trackError(
						"login",
						"pending_social_login_unreadable",
						// JSON.parse and a read off its result throw only Errors.
						(parseError as Error).message
					);
					sessionStorage.removeItem("pending_social_login");
				}
			}
			trackFunnelEvent("login", "oauth_failed", { provider, error });
			trackError("login", "oauth_failed", `OAuth failed: ${error}`);
		}
	}, [query.error]);

	// Auto-close modal if user is already authenticated (pre-logged in sessions)
	useEffect(() => {
		if (nextAuthStatus === "loading") return;

		// If authenticated and not on success screens, close modal
		if (nextAuthStatus === "authenticated" && loginOpen) {
			if (!LOGIN_IN_PROGRESS_VIEWS.includes(loginView)) {
				setLoginOpen(false);
				changeHash("");
			}
		}
	}, [nextAuthStatus, loginOpen, loginView, setLoginOpen]);


	// Computed value for email flow class
	const emailAddtl = loginView === "login-otp" && fields.phone.match(/\@/) ? "emailflow" : "";

	return {
		// State
		loginView,
		loginOpen,
		submitting,
		fields,
		errors,
		focus,
		filled,
		otpCode,
		otpError,
		theme,
		themeHydrated,
		profile,
		loggedIn,
		tempShippingAddress,
		emailAddtl,

		// Setters
		setFields,
		setErrors,
		setFocus,
		setFilled,
		setOTPCode: changeOTPCode,
		updateField,
		setFieldFocus,
		setPopupContents,
		setPopupOpen,

		// Handlers
		handleSubmit,
		resendCode,
		closeLogin,
		handleGoogleLogin,
		loginWithFacebook,
		loginWithApple,
		toggleTheme,

		// Phone validation helper (for PhoneInput component)
		checkPhone: (_e: React.ChangeEvent<HTMLInputElement>) => {
			if (!fields.phone.includes("@")) {
				checkPhone(fields.phone, fields, errors, setErrors);
			}
		},

		// Ref
		fireworksRef,
	};
};

// Re-export types that might be needed by the component
export type { LoginFormFields, FormErrors, FormFocus };
